import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import AgentPlayerDetailPanel from '../components/players/AgentPlayerDetailPanel';
import { fetchAgentPlayer, fetchAgentPlayerTransactions } from '../services/api';
import type { AgentPlayerDetail, AgentPlayerWalletTransaction } from '../types';

export default function AgentPlayerDetailPage() {
  const { playerId } = useParams<{ playerId: string }>();
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState<AgentPlayerDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyRows, setHistoryRows] = useState<AgentPlayerWalletTransaction[]>([]);

  const load = useCallback(async () => {
    if (!playerId) {
      setNotFound(true);
      setPlayer(null);
      setLoading(false);
      setHistoryLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    setHistoryRows([]);
    let playerOk = false;
    try {
      const playerRes = await fetchAgentPlayer(playerId);
      if (playerRes.success && playerRes.data) {
        setPlayer(playerRes.data);
        setNotFound(false);
        playerOk = true;
      } else {
        setPlayer(null);
        setNotFound(true);
      }
    } catch {
      setPlayer(null);
      setNotFound(true);
      toast.error('Could not load Player');
    } finally {
      setLoading(false);
    }

    if (!playerId || !playerOk) return;
    setHistoryLoading(true);
    try {
      const txRes = await fetchAgentPlayerTransactions(playerId, { limit: 400 });
      if (txRes.success && Array.isArray(txRes.data)) {
        setHistoryRows(txRes.data);
      } else {
        setHistoryRows([]);
      }
    } catch {
      setHistoryRows([]);
      toast.error('Could not load transactions');
    } finally {
      setHistoryLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <AppLayout>
      <Link
        to="/players"
        className="mb-4 inline-flex items-center gap-0.5 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        Back to Players
      </Link>

      {loading ? (
        <PageLoadingIndicator variant="page" accentColor="#059669" />
      ) : notFound || !player ? (
        <div className="rounded-xl border border-emerald-100 bg-white p-8 text-center shadow-sm">
          <p className="text-sm font-medium text-gray-900">Player not found</p>
          <p className="mt-1 text-sm text-gray-500">This Player may not belong to your Shops.</p>
          <Link to="/players" className="mt-4 inline-block text-sm font-semibold text-emerald-700 hover:underline">
            Back to Player list
          </Link>
        </div>
      ) : (
        <AgentPlayerDetailPanel player={player} historyRows={historyRows} historyLoading={historyLoading} />
      )}
    </AppLayout>
  );
}
